import { Fragment, useEffect, useState } from "react";
import { panelCls } from "@/utils/panelCls";

// ── 4-단계 AI 파이프라인 정의 ─────────────────────────────────────────────────
// 각 threshold: startedAt 기준 경과 초 (이 시점부터 해당 단계를 "현재"로 표시)
const STAGES = [
  { id: 0, label: "Peak 추출", threshold: 0, sublabel: null },
  {
    id: 1,
    label: "화합물 동정 (JSD)",
    threshold: 1,
    sublabel: "인하대 SMILES_pred",
  },
  {
    id: 2,
    label: "물성 예측 (KROVEX)",
    threshold: 8,
    sublabel: "VP / Solubility",
  },
  {
    id: 3,
    label: "위험 분류 (RoMSH)",
    threshold: 11,
    sublabel: "인하대 트랜스포머",
  },
] as const;

type StageId = (typeof STAGES)[number]["id"];

function currentStageIndex(elapsed: number): StageId {
  if (elapsed < 1) return 0;
  if (elapsed < 8) return 1;
  if (elapsed < 11) return 2;
  return 3;
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ── 타입 ──────────────────────────────────────────────────────────────────────

interface Props {
  status: "pending" | "running" | "completed" | "failed";
  startedAt: string | null; // ISO datetime
}

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export default function AnalysisProgressIndicator({
  status,
  startedAt,
}: Props) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (status === "completed" || status === "failed") return;

    // 백엔드가 naive UTC ISO ("2026-05-16T01:35:46.844441")를 내려주는 경우 보정.
    // 'Z'/'+'/'-' offset이 없으면 Date()가 로컬타임으로 해석 → 9시간 오차 발생.
    // → 명시적으로 UTC로 파싱 (Z suffix 부착).
    const parseStartedAt = (iso: string | null): number => {
      if (!iso) return Date.now();
      const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
      const normalized = hasTz ? iso : `${iso}Z`;
      const ms = new Date(normalized).getTime();
      return Number.isFinite(ms) ? ms : Date.now();
    };
    const startMs = parseStartedAt(startedAt);
    const tick = () =>
      setElapsed(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [status, startedAt]);

  const currentIdx = currentStageIndex(elapsed);

  return (
    <div
      data-testid="analysis-progress-indicator"
      className={panelCls("primary", "space-y-1")}
    >
      {/* 헤더: 섹션 라벨 + elapsed 타이머 */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-black uppercase tracking-widest opacity-60">
          AI 분석 진행 중
        </span>
        <span
          data-testid="elapsed-timer"
          className="text-xs font-mono text-slate-500 tabular-nums"
        >
          {formatElapsed(elapsed)}
        </span>
      </div>

      {/* 4-단계 dots + 연결선 */}
      <div className="flex items-center justify-between gap-2 mb-3">
        {STAGES.map((stage, idx) => {
          const isDone = idx < currentIdx;
          const isCurrent = idx === currentIdx;
          return (
            <Fragment key={stage.id}>
              <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
                <div
                  data-testid={`stage-dot-${idx}`}
                  className={[
                    "w-3 h-3 rounded-full transition-all",
                    isDone && "bg-emerald-500",
                    isCurrent && "bg-blue-500 animate-pulse",
                    !isDone && !isCurrent && "bg-slate-200",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                />
                <span
                  className={[
                    "text-[10px] text-center leading-tight truncate w-full",
                    isCurrent
                      ? "font-semibold text-slate-800"
                      : "text-slate-500",
                  ].join(" ")}
                >
                  {stage.label}
                </span>
              </div>
              {idx < STAGES.length - 1 && (
                <div
                  aria-hidden="true"
                  className={[
                    "h-px flex-1 shrink-0 -mt-4",
                    idx < currentIdx ? "bg-emerald-300" : "bg-slate-200",
                  ].join(" ")}
                />
              )}
            </Fragment>
          );
        })}
      </div>

      {/* 현재 단계 텍스트 */}
      <div data-testid="current-stage-label" className="text-sm text-slate-700">
        <span className="font-semibold">현재:</span> {STAGES[currentIdx].label}{" "}
        중
        {STAGES[currentIdx].sublabel && (
          <span
            data-testid="current-stage-sublabel"
            className="ml-2 text-xs text-slate-500"
          >
            — {STAGES[currentIdx].sublabel}
          </span>
        )}
      </div>

      {/* 안내 문구 */}
      <p className="text-xs text-slate-400 mt-2">
        예상 소요 ~15초 · 자동 갱신 (3초마다)
      </p>
    </div>
  );
}
